/**
 * SystemsTab Component
 *
 * Allows contractors to configure system options for assessments:
 * - Enable/disable system types
 * - Create up to 9 options per gas system type (3 SEER × 3 AFUE combinations)
 * - Create up to 3 options per non-gas system type
 * - Mark options as condensing furnaces (gas systems only)
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

// System type definitions
const SYSTEM_TYPES = [
  {
    id: 'centralAC',
    label: 'Central AC (Electric Cooling Only)',
    icon: '❄️',
    requiresGas: false,
    fields: ['seer2']
  },
  {
    id: 'heatPump',
    label: 'Heat Pump (Electric Heating + Cooling)',
    icon: '🔥❄️',
    requiresGas: false,
    fields: ['seer2']
  },
  {
    id: 'heatPumpPackage',
    label: 'Heat Pump Package Unit (Electric Heating + Cooling)',
    icon: '📦🔥❄️',
    requiresGas: false,
    fields: ['seer2']
  },
  {
    id: 'furnaceAC',
    label: 'Gas Furnace + AC (Dual Fuel)',
    icon: '🔥❄️',
    requiresGas: true,
    fields: ['seer2', 'afue']
  },
  {
    id: 'furnaceACPackage',
    label: 'Gas Furnace + AC Package Unit (Dual Fuel)',
    icon: '📦🔥❄️',
    requiresGas: true,
    fields: ['seer2', 'afue']
  },
  {
    id: 'furnaceOnly',
    label: 'Gas Furnace Only',
    icon: '🔥',
    requiresGas: true,
    fields: ['afue']
  },
  {
    id: 'boiler',
    label: 'Boiler (Hydronic Heating)',
    icon: '♨️',
    requiresGas: true,
    fields: ['afue']
  },
  {
    id: 'ductlessMini',
    label: 'Ductless Mini-Split',
    icon: '🌡️',
    requiresGas: false,
    fields: ['seer2']
  }
];

export default function SystemsTab() {
  const { contractor } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // System type settings (enabled/disabled)
  const [systemTypeSettings, setSystemTypeSettings] = useState({});

  // System options for each type
  const [systemOptions, setSystemOptions] = useState({});

  // Editing state
  const [editingSystemType, setEditingSystemType] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [showOptionModal, setShowOptionModal] = useState(false);

  // Load system type settings and options
  useEffect(() => {
    if (contractor?.id) {
      loadSystemData();
    }
  }, [contractor]);

  const loadSystemData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load system type settings
      const { data: settings, error: settingsError } = await supabase
        .from('system_type_settings')
        .select('*')
        .eq('contractor_id', contractor.id);

      if (settingsError) throw settingsError;

      // Convert to object keyed by system_type
      const settingsObj = {};
      settings?.forEach(s => {
        settingsObj[s.system_type] = s;
      });

      // Initialize all system types (default DISABLED if not set - forces intentional enablement)
      SYSTEM_TYPES.forEach(type => {
        if (!settingsObj[type.id]) {
          settingsObj[type.id] = {
            system_type: type.id,
            is_enabled: false,  // Default to disabled to force intentional configuration
            contractor_id: contractor.id
          };
        }
      });

      setSystemTypeSettings(settingsObj);

      // Load system options
      const { data: options, error: optionsError } = await supabase
        .from('system_options')
        .select('*')
        .eq('contractor_id', contractor.id)
        .order('option_number', { ascending: true });

      if (optionsError) throw optionsError;

      // Group options by system_type
      const optionsObj = {};
      options?.forEach(opt => {
        if (!optionsObj[opt.system_type]) {
          optionsObj[opt.system_type] = [];
        }
        optionsObj[opt.system_type].push(opt);
      });

      setSystemOptions(optionsObj);
    } catch (err) {
      console.error('Error loading system data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle system type enabled/disabled
  const toggleSystemType = async (systemTypeId) => {
    try {
      setSaving(true);
      setError(null);

      const currentSetting = systemTypeSettings[systemTypeId];
      const newEnabled = !currentSetting.is_enabled;

      if (currentSetting.id) {
        // Update existing setting
        const { error: updateError } = await supabase
          .from('system_type_settings')
          .update({ is_enabled: newEnabled })
          .eq('id', currentSetting.id);

        if (updateError) throw updateError;
      } else {
        // Insert new setting
        const { data, error: insertError } = await supabase
          .from('system_type_settings')
          .insert({
            contractor_id: contractor.id,
            system_type: systemTypeId,
            is_enabled: newEnabled
          })
          .select()
          .single();

        if (insertError) throw insertError;

        // Update local state with new ID
        setSystemTypeSettings(prev => ({
          ...prev,
          [systemTypeId]: data
        }));
      }

      // Update local state
      setSystemTypeSettings(prev => ({
        ...prev,
        [systemTypeId]: { ...prev[systemTypeId], is_enabled: newEnabled }
      }));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error toggling system type:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Open modal to add/edit system option
  const openOptionModal = (systemTypeId, optionNumber = null) => {
    setEditingSystemType(systemTypeId);

    if (optionNumber !== null) {
      // Edit existing option
      const existing = systemOptions[systemTypeId]?.find(opt => opt.option_number === optionNumber);
      setEditingOption(existing || null);
    } else {
      // Add new option - find next available option number
      const existingOptions = systemOptions[systemTypeId] || [];
      const nextNumber = existingOptions.length + 1;

      // Gas systems can have up to 9 options, non-gas systems up to 3
      const systemTypeDef = SYSTEM_TYPES.find(t => t.id === systemTypeId);
      const maxOptions = systemTypeDef?.requiresGas ? 9 : 3;

      if (nextNumber > maxOptions) {
        setError(`Maximum ${maxOptions} options per system type`);
        return;
      }

      setEditingOption({
        system_type: systemTypeId,
        option_number: nextNumber,
        option_name: '',
        seer2_rating: null,
        afue_rating: null,
        starting_price: '',
        monthly_payment: '',
        finance_months: 60,
        interest_rate: '',
        is_enabled: true,
        is_condensing: false  // For gas systems
      });
    }

    setShowOptionModal(true);
  };

  // Close option modal
  const closeOptionModal = () => {
    setShowOptionModal(false);
    setEditingSystemType(null);
    setEditingOption(null);
  };

  // Save system option
  const saveOption = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validation
      if (!editingOption.option_name?.trim()) {
        setError('Option name is required');
        return;
      }

      if (!editingOption.starting_price || parseFloat(editingOption.starting_price) <= 0) {
        setError('Starting price is required and must be greater than 0');
        return;
      }

      // Get system type definition
      const systemTypeDef = SYSTEM_TYPES.find(t => t.id === editingSystemType);

      // Validate required efficiency fields
      if (systemTypeDef.fields.includes('seer2') && !editingOption.seer2_rating) {
        setError('SEER2 rating is required');
        return;
      }

      if (systemTypeDef.fields.includes('afue') && !editingOption.afue_rating) {
        setError('AFUE rating is required');
        return;
      }

      const optionData = {
        contractor_id: contractor.id,
        system_type: editingOption.system_type,
        option_number: editingOption.option_number,
        option_name: editingOption.option_name.trim(),
        seer2_rating: editingOption.seer2_rating || null,
        afue_rating: editingOption.afue_rating || null,
        starting_price: parseFloat(editingOption.starting_price),
        monthly_payment: editingOption.monthly_payment ? parseFloat(editingOption.monthly_payment) : null,
        finance_months: editingOption.finance_months ? parseInt(editingOption.finance_months) : null,
        interest_rate: editingOption.interest_rate ? parseFloat(editingOption.interest_rate) : null,
        is_enabled: editingOption.is_enabled,
        is_condensing: systemTypeDef.requiresGas ? (editingOption.is_condensing || false) : null
      };

      if (editingOption.id) {
        // Update existing
        const { error: updateError } = await supabase
          .from('system_options')
          .update(optionData)
          .eq('id', editingOption.id);

        if (updateError) throw updateError;
      } else {
        // Insert new
        const { error: insertError } = await supabase
          .from('system_options')
          .insert(optionData);

        if (insertError) throw insertError;
      }

      // Reload data
      await loadSystemData();

      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);

      closeOptionModal();
    } catch (err) {
      console.error('Error saving system option:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete system option
  const deleteOption = async (optionId) => {
    if (!confirm('Are you sure you want to delete this system option?')) return;

    try {
      setSaving(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('system_options')
        .delete()
        .eq('id', optionId);

      if (deleteError) throw deleteError;

      await loadSystemData();

      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error deleting system option:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '16px', color: '#64748b' }}>Loading system configuration...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '20px', marginBottom: '10px' }}>Systems Configuration</h2>
        <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
          Configure system options for each system type. Gas systems support up to 9 options (to cover different SEER/AFUE combinations),
          while non-gas systems support up to 3 options. These options will be used during assessments
          to compare energy savings and provide homeowners with detailed recommendations.
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div style={{
          marginBottom: '20px',
          padding: '12px',
          background: '#dcfce7',
          border: '2px solid #22c55e',
          borderRadius: '8px',
          color: '#14532d',
          fontSize: '14px'
        }}>
          ✅ <strong>Saved successfully!</strong>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          marginBottom: '20px',
          padding: '12px',
          background: '#fee2e2',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          color: '#dc2626',
          fontSize: '14px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* System Type Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {SYSTEM_TYPES.map(systemType => {
          const setting = systemTypeSettings[systemType.id] || {};
          const isEnabled = setting.is_enabled !== false;
          const options = systemOptions[systemType.id] || [];

          return (
            <div
              key={systemType.id}
              className="card"
              style={{
                opacity: isEnabled ? 1 : 0.6,
                border: isEnabled ? '2px solid #e0e6ed' : '2px solid #f1f5f9'
              }}
            >
              {/* System Type Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                paddingBottom: '15px',
                borderBottom: '2px solid #f1f5f9'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '28px' }}>{systemType.icon}</span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
                      {systemType.label}
                    </h3>
                    {systemType.requiresGas && (
                      <span style={{
                        fontSize: '12px',
                        color: '#f59e0b',
                        background: '#fef3c7',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        marginTop: '4px',
                        display: 'inline-block'
                      }}>
                        Requires Gas/Propane
                      </span>
                    )}
                  </div>
                </div>

                {/* Enable/Disable Toggle */}
                <button
                  type="button"
                  onClick={() => toggleSystemType(systemType.id)}
                  disabled={saving}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    background: isEnabled
                      ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                      : '#94a3b8',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {isEnabled ? '✓ Enabled' : '✕ Disabled'}
                </button>
              </div>

              {/* System Options */}
              {isEnabled && (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                      System Options ({options.length}/{systemType.requiresGas ? 9 : 3})
                    </div>
                    {options.length < (systemType.requiresGas ? 9 : 3) && (
                      <button
                        type="button"
                        onClick={() => openOptionModal(systemType.id)}
                        disabled={saving}
                        className="btn btn-primary"
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                      >
                        + Add Option
                      </button>
                    )}
                  </div>

                  {/* Options List */}
                  {options.length === 0 ? (
                    <div style={{
                      padding: '30px',
                      textAlign: 'center',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '2px dashed #e0e6ed'
                    }}>
                      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                        No system options configured yet
                      </div>
                      <button
                        type="button"
                        onClick={() => openOptionModal(systemType.id)}
                        disabled={saving}
                        className="btn btn-primary"
                        style={{ fontSize: '14px' }}
                      >
                        + Add First Option
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {options.map(option => (
                        <div
                          key={option.id}
                          style={{
                            padding: '16px',
                            background: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e0e6ed',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#2c3e50',
                              marginBottom: '8px'
                            }}>
                              Option {option.option_number}: {option.option_name}
                            </div>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                              gap: '12px',
                              fontSize: '13px',
                              color: '#64748b'
                            }}>
                              {option.seer2_rating && (
                                <div>
                                  <strong>SEER2:</strong> {option.seer2_rating}
                                </div>
                              )}
                              {option.afue_rating && (
                                <div>
                                  <strong>AFUE:</strong> {option.afue_rating}%
                                  {option.is_condensing && (
                                    <span style={{
                                      marginLeft: '6px',
                                      padding: '2px 6px',
                                      background: '#fef3c7',
                                      color: '#92400e',
                                      fontSize: '11px',
                                      borderRadius: '4px',
                                      fontWeight: '600'
                                    }}>
                                      Condensing
                                    </span>
                                  )}
                                </div>
                              )}
                              <div>
                                <strong>Price:</strong> ${option.starting_price.toLocaleString()}
                              </div>
                              {option.monthly_payment && (
                                <div>
                                  <strong>Monthly:</strong> ${option.monthly_payment}/mo for {option.finance_months} months @ {option.interest_rate}%
                                </div>
                              )}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                            <button
                              type="button"
                              onClick={() => openOptionModal(systemType.id, option.option_number)}
                              disabled={saving}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                background: 'white',
                                color: '#475569',
                                fontSize: '13px',
                                cursor: 'pointer'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteOption(option.id)}
                              disabled={saving}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid #fecaca',
                                background: 'white',
                                color: '#dc2626',
                                fontSize: '13px',
                                cursor: 'pointer'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Disabled Message */}
              {!isEnabled && (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  color: '#64748b',
                  fontSize: '14px'
                }}>
                  This system type is disabled and will not appear in assessment dropdowns.
                  Click "Disabled" above to enable it.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Option Edit Modal */}
      {showOptionModal && editingOption && (
        <SystemOptionModal
          systemType={SYSTEM_TYPES.find(t => t.id === editingSystemType)}
          option={editingOption}
          setOption={setEditingOption}
          onSave={saveOption}
          onCancel={closeOptionModal}
          saving={saving}
        />
      )}
    </div>
  );
}

// System Option Modal Component
function SystemOptionModal({ systemType, option, setOption, onSave, onCancel, saving }) {
  const updateField = (field, value) => {
    setOption(prev => ({ ...prev, [field]: value }));
  };

  // Calculate monthly payment using standard loan formula
  const calculateMonthlyPayment = () => {
    const principal = parseFloat(option.starting_price);
    const annualRate = parseFloat(option.interest_rate);
    const months = parseInt(option.finance_months);

    // Validate inputs
    if (!principal || principal <= 0 || !months || months <= 0) {
      return;
    }

    // Handle 0% interest rate (simple division)
    if (!annualRate || annualRate === 0) {
      const payment = principal / months;
      updateField('monthly_payment', payment.toFixed(2));
      return;
    }

    // Standard loan payment formula: M = P * [r(1 + r)^n] / [(1 + r)^n - 1]
    const monthlyRate = annualRate / 100 / 12;
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);

    updateField('monthly_payment', payment.toFixed(2));
  };

  // Check if all required fields for calculation are filled
  const canCalculate = option.starting_price && parseFloat(option.starting_price) > 0 &&
                       option.finance_months && parseInt(option.finance_months) > 0 &&
                       option.interest_rate !== null && option.interest_rate !== undefined && option.interest_rate !== '';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        maxWidth: '700px',
        width: '100%',
        padding: '30px',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h3 style={{ fontSize: '22px', marginBottom: '10px', color: '#2c3e50' }}>
          {option.id ? 'Edit' : 'Add'} System Option
        </h3>
        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '25px' }}>
          {systemType.icon} {systemType.label} - Option {option.option_number}
        </div>

        {/* Option Name */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
            Option Name <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            className="input"
            value={option.option_name}
            onChange={(e) => updateField('option_name', e.target.value)}
            placeholder="e.g., 15 SEER2, 17 SEER2 Premium"
            style={{ width: '100%' }}
          />
        </div>

        {/* Efficiency Ratings */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
          {systemType.fields.includes('seer2') && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                SEER2 Rating <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="number"
                className="input"
                value={option.seer2_rating || ''}
                onChange={(e) => updateField('seer2_rating', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="15.0"
                step="0.5"
                min="10"
                max="30"
                style={{ width: '100%' }}
              />
            </div>
          )}

          {systemType.fields.includes('afue') && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                AFUE Rating <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="number"
                className="input"
                value={option.afue_rating || ''}
                onChange={(e) => updateField('afue_rating', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="95.0"
                step="0.5"
                min="70"
                max="99"
                style={{ width: '100%' }}
              />
            </div>
          )}

          {/* Condensing Furnace Checkbox (for gas systems) */}
          {systemType.requiresGas && (
            <div style={{ marginTop: '15px', padding: '15px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                cursor: 'pointer',
                fontSize: '14px'
              }}>
                <input
                  type="checkbox"
                  checked={option.is_condensing || false}
                  onChange={(e) => updateField('is_condensing', e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    marginRight: '10px',
                    marginTop: '2px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '4px' }}>
                    Condensing Furnace (90%+ AFUE)
                  </div>
                  <div style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.4' }}>
                    Check this if this option is a condensing furnace that requires PVC vent pipes.
                    Leave unchecked for conventional furnaces (80% AFUE) that use chimney venting.
                    This helps filter options based on home compatibility during assessments.
                  </div>
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Starting Price */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
            Starting Price <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b',
              fontSize: '16px'
            }}>
              $
            </span>
            <input
              type="number"
              className="input"
              value={option.starting_price}
              onChange={(e) => updateField('starting_price', e.target.value)}
              placeholder="12500"
              step="100"
              min="0"
              style={{ width: '100%', paddingLeft: '30px' }}
            />
          </div>
        </div>

        {/* Financing Section */}
        <div style={{
          padding: '20px',
          background: '#f0f9ff',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#0c4a6e', marginBottom: '15px' }}>
            Financing Details (Optional)
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Monthly Payment
              </label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <span style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#64748b',
                    fontSize: '16px'
                  }}>
                    $
                  </span>
                  <input
                    type="number"
                    className="input"
                    value={option.monthly_payment || ''}
                    onChange={(e) => updateField('monthly_payment', e.target.value)}
                    placeholder="215"
                    step="1"
                    min="0"
                    style={{ width: '100%', paddingLeft: '30px' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={calculateMonthlyPayment}
                  disabled={!canCalculate}
                  title={!canCalculate ? 'Enter starting price, interest rate, and finance term to calculate' : 'Calculate monthly payment'}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: canCalculate
                      ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                      : '#cbd5e1',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: canCalculate ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap',
                    height: '44px'
                  }}
                >
                  Calculate
                </button>
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                {canCalculate
                  ? 'Click Calculate to auto-fill based on price, rate, and term. You can override this value manually.'
                  : 'Enter starting price, interest rate, and finance term above to enable calculation.'
                }
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Finance Term (Months)
              </label>
              <input
                type="number"
                className="input"
                value={option.finance_months || ''}
                onChange={(e) => updateField('finance_months', e.target.value)}
                placeholder="60"
                step="12"
                min="0"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Interest Rate (%)
              </label>
              <input
                type="number"
                className="input"
                value={option.interest_rate || ''}
                onChange={(e) => updateField('interest_rate', e.target.value)}
                placeholder="9.99"
                step="0.01"
                min="0"
                max="100"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{
              padding: '12px 24px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              background: 'white',
              color: '#374151',
              fontSize: '15px',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="btn btn-primary"
            style={{ padding: '12px 24px', fontSize: '15px' }}
          >
            {saving ? 'Saving...' : 'Save Option'}
          </button>
        </div>
      </div>
    </div>
  );
}
